import React, { useCallback, useEffect, useState } from "react";
import {
    listParameterRequests,
    setParameterRequestStatus,
    ParameterRequestDTO,
    ParameterRequestStatus,
} from "../../api/ParameterRequestAPI";
import { getUserMessage, logApiError } from "../../api/apiError";

interface Props {
    token: string;
    /** Opens the request's category (e.g. select it in the tree for editing). */
    onOpenCategory?: (categoryId: string) => void;
}

/** Admin queue of parameter requests: review, jump to the category, mark done. */
const ParameterRequestAdminList: React.FC<Props> = ({ token, onOpenCategory }) => {
    const [status, setStatus] = useState<ParameterRequestStatus>("OPEN");
    const [requests, setRequests] = useState<ParameterRequestDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRequests(await listParameterRequests(token, status));
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }, [token, status]);

    useEffect(() => { load(); }, [load]);

    const markStatus = async (r: ParameterRequestDTO, next: ParameterRequestStatus) => {
        setError(null);
        try {
            await setParameterRequestStatus(token, r.id, r.version, next);
            setRequests((prev) => prev.filter((x) => x.id !== r.id));
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
            await load(); // refresh versions after a conflict
        }
    };

    return (
        <div style={{ marginTop: "1rem", border: "1px solid var(--border-color)", borderRadius: "var(--radius-md, 6px)", padding: "0.75rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
                <strong>Parameter requests</strong>
                <select value={status} onChange={(e) => setStatus(e.target.value as ParameterRequestStatus)}>
                    <option value="OPEN">Open</option>
                    <option value="DONE">Done</option>
                </select>
                <button type="button" className="btn-sm btn-secondary" onClick={load} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>

            {error && <div className="error-message" style={{ marginBottom: "0.5rem" }}>{error}</div>}

            {requests.length === 0 && !loading ? (
                <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                    No {status.toLowerCase()} requests.
                </div>
            ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    {requests.map((r) => (
                        <li key={r.id} style={{ border: "1px solid var(--border-color)", borderRadius: "4px", padding: "0.5rem" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                <strong>{r.categoryName}</strong>
                                {r.createdAt && (
                                    <span style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                                        {new Date(r.createdAt).toLocaleDateString()}
                                    </span>
                                )}
                                <span style={{ marginLeft: "auto", display: "flex", gap: "0.4rem" }}>
                                    {onOpenCategory && (
                                        <button type="button" className="btn-sm btn-secondary" onClick={() => onOpenCategory(r.categoryId)}>
                                            Open category
                                        </button>
                                    )}
                                    {status === "OPEN" ? (
                                        <button type="button" className="btn-sm btn-success" onClick={() => markStatus(r, "DONE")}>
                                            Mark done
                                        </button>
                                    ) : (
                                        <button type="button" className="btn-sm btn-secondary" onClick={() => markStatus(r, "OPEN")}>
                                            Reopen
                                        </button>
                                    )}
                                </span>
                            </div>
                            <div style={{ whiteSpace: "pre-wrap", marginTop: "0.35rem", fontSize: "0.9rem" }}>{r.text}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default ParameterRequestAdminList;